import { useSelector } from "react-redux";
import { DisruptionOffersSelectors } from "@b2bportal/core-disruption";
import {
  getSelectedFarePaxPricing,
  getTripCategory,
  FlightShopSelectors,
} from "@b2bportal/core-flights";
import { FlightsCfarOffersSelectors } from "@b2bportal/core-flights-cfar-offers";
import { SelectedStatus } from "@b2bportal/core-types";
import { DisruptionOfferQuoteSelectionEnum } from "@b2bportal/core-types";
import {
  FlightSelectors,
  useCheckoutStateSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { type Prices, TripCategory } from "@hopper-b2b/types";
import { twoFractionOptions } from "@hsbc/utilities";
import { usePriceDisplay } from "@tenant/ui";
import type { PriceBreakdownLineItemProps } from "../../PriceBreakdownLineItem";

// assumes same currency and common account key
const addPrices = (price1: Prices, price2: Prices): Prices => {
  const accountKey = Object.keys(price1.accountSpecific);
  if (accountKey == null) {
    return price1;
  }
  const accountValue1 = price1.accountSpecific[accountKey[0]];
  const accountValue2 = price2.accountSpecific[accountKey[0]];

  return {
    fiat: {
      ...price1.fiat,
      value: price1.fiat.value + price2.fiat.value,
    },
    accountSpecific: {
      accountKey: {
        ...accountValue1,
        value: accountValue1.value + accountValue2.value,
      },
    },
  };
};

// assumes same currency and common account key
const addDiscount = (price1: Prices, price2: Prices): Prices => {
  const accountKey = Object.keys(price1.accountSpecific);
  if (accountKey == null) {
    return price1;
  }
  const accountValue1 = price1.accountSpecific[accountKey[0]];
  const accountValue2 = price2.accountSpecific[accountKey[0]];

  return {
    fiat: {
      ...price1.fiat,
      value: price1.fiat.value - price2.fiat.value,
    },
    accountSpecific: {
      accountKey: {
        ...accountValue1,
        value: accountValue1.value + accountValue2.value,
      },
    },
  };
};

interface UsePriceBreakdownReturn {
  priceData: Array<PriceBreakdownLineItemProps>;
  tripCategoryDescription: string;
  isMultiTicket: boolean;
}

export const usePriceBreakdown = (): UsePriceBreakdownReturn => {
  const { t, formatFiatCurrency } = useI18nContext();
  const tripCategory = useSelector(getTripCategory);

  const checkoutFareDetails = useCheckoutStateSelector(
    FlightSelectors.getSelectedFareDetails
  );
  const checkoutPaxPricing = checkoutFareDetails?.paxPricings?.find?.(
    (pax) => pax.paxType === "ADT"
  );

  const shopPaxPricing = useSelector(getSelectedFarePaxPricing);
  const paxPricing = checkoutPaxPricing || shopPaxPricing;
  const selectedDisruptionOffer = useSelector(
    DisruptionOffersSelectors.getSelectedDisruptionOffer
  );
  const isDisruptionOfferAttached =
    selectedDisruptionOffer?.selection ===
    DisruptionOfferQuoteSelectionEnum.Selected;

  const selectedCfarOffer = useSelector(
    FlightsCfarOffersSelectors.getSelectedCfarOffer
  );

  const fintechPriceData = () => {
    const priceItems: PriceBreakdownLineItemProps[] = [];

    if (isDisruptionOfferAttached) {
      priceItems.push({
        id: "disruption",
        label: t("core-disruption.productName"),
        value: formatFiatCurrency(
          selectedDisruptionOffer.quote.pricePerPaxLocalized?.fiat,
          twoFractionOptions
        ),
      });
    }
    if (selectedCfarOffer.status === SelectedStatus.SELECTED) {
      priceItems.push({
        id: "cfar",
        label: t("core-flights-cfar.productName"),
        value: formatFiatCurrency(
          selectedCfarOffer.offer.premiumAmount.fiat,
          twoFractionOptions
        ),
      });
    }
    return priceItems;
  };

  const isOutgoingMultiTicket = useSelector(
    FlightShopSelectors.isOutgoingMultiTicket
  );
  const isReturnMultiTicket = useSelector(
    FlightShopSelectors.isReturnMultiTicket
  );
  const isMultiTicket = isOutgoingMultiTicket || isReturnMultiTicket;

  const { baseAmount, taxAmount, total: fareTotal } = paxPricing.pricing;

  let total = fareTotal;
  total =
    selectedCfarOffer.status === SelectedStatus.SELECTED
      ? addPrices(total, selectedCfarOffer.offer.premiumAmount)
      : total;
  total = isDisruptionOfferAttached
    ? addPrices(total, selectedDisruptionOffer.quote.pricePerPaxLocalized)
    : total;

  const { combinedText } = usePriceDisplay({
    prices: total,
    formatOptions: twoFractionOptions,
  });

  const priceData = [
    {
      id: "base",
      label: t("core-checkout.priceBreakdown.baseFare"),
      value: formatFiatCurrency(baseAmount.fiat, twoFractionOptions),
    },
    {
      id: "taxes",
      label: t("core-checkout.priceBreakdown.taxesFees"),
      value: formatFiatCurrency(taxAmount.fiat, twoFractionOptions),
    },
    ...fintechPriceData(),
    {
      id: "total",
      label: t("core-checkout.priceBreakdown.total"),
      value: combinedText,
    },
  ];

  return {
    priceData,
    tripCategoryDescription: t(
      `filtersHeader.${
        tripCategory === TripCategory.ROUND_TRIP ? "roundTrip" : "oneWay"
      }Subtitle`
    ),
    isMultiTicket,
  };
};
